import type {ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {DeepPartial} from '@/global';
import type {VideoItemObject} from '@PosterWhiteboard/items/video-item/video-item.types';
import {isVideoLoopingEnabledForItem} from '@PosterWhiteboard/items/video-item/video-item.types';
import type {SlideshowItemObject} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import {isSlideshowItemObject, isVideoItemObject} from '@PosterWhiteboard/items/item/item.library';
import {positiveModulo, quantizeTimeSeconds} from '@Utils/math.util';

/**
 * Pure timing math over serialized item objects, intentionally free of any Fabric/canvas value
 * imports so `page-duration.library`, the timeline UI, and unit tests can depend on it without
 * loading the editor's heavy item pipeline. General item helpers and the `is*ItemObject` type
 * guards live in `item.library`.
 */

export function getSlideshowDurationFromObject(slideshowObject: SlideshowItemObject): number {
  let duration = 0;
  for (const slideId of slideshowObject.slides.slidesOrder) {
    duration += slideshowObject.slides.slidesHashMap[slideId]?.slideDuration ?? 0;
  }
  return duration;
}

/**
 * Playback duration of a video / video-background item — the trim window
 * `(endTime - startTime)` divided by playback speed. Speed defaults to 1 and is
 * clamped to a positive value to avoid division-by-zero when state is mid-init.
 */
export function getMotionItemPlaybackDuration(videoItemObject: Pick<VideoItemObject, 'startTime' | 'endTime' | 'speed'>): number {
  const speed = videoItemObject.speed > 0 ? videoItemObject.speed : 1;
  return (videoItemObject.endTime - videoItemObject.startTime) / speed;
}

/** A boundary landing this close to either span edge is excluded — the clip starts/ends there, nothing restarts. */
const LOOP_BOUNDARY_EPSILON_SECONDS = 0.01;

/**
 * Offsets (seconds from the item's on-page start) at which a looping video restarts
 * within a span of the given duration. A nonzero loopStartOffset shortens the first
 * (partial) pass, pulling every boundary earlier by the same amount. Empty for
 * non-looping (background) videos.
 */
export function getVideoItemLoopBoundaryOffsets(
  videoItemObject: Pick<VideoItemObject, 'startTime' | 'endTime' | 'speed' | 'isBackground' | 'loopStartOffset'>,
  spanDurationSeconds: number
): number[] {
  if (!isVideoLoopingEnabledForItem(videoItemObject)) {
    return [];
  }

  const singlePassDuration = getMotionItemPlaybackDuration(videoItemObject);
  if (singlePassDuration <= 0) {
    return [];
  }

  const speed = videoItemObject.speed > 0 ? videoItemObject.speed : 1;
  const firstPassDuration = singlePassDuration - videoItemObject.loopStartOffset / speed;
  const boundaryOffsets: number[] = [];
  for (let boundaryOffset = firstPassDuration; boundaryOffset < spanDurationSeconds - LOOP_BOUNDARY_EPSILON_SECONDS; boundaryOffset += singlePassDuration) {
    if (boundaryOffset > LOOP_BOUNDARY_EPSILON_SECONDS) {
      boundaryOffsets.push(boundaryOffset);
    }
  }
  return boundaryOffsets;
}

/**
 * The loop-phase offset a looping video takes after its bar start moves by
 * `pageTimeDeltaSeconds` while every loop boundary stays fixed in page time — the
 * rule behind the left-handle resize and the split tail. Source seconds, normalized
 * to [0, trimWindow); quantized onto the millisecond grid all stored times live on.
 */
export function getLoopStartOffsetAfterStartShift(
  videoItemObject: Pick<VideoItemObject, 'loopStartOffset' | 'startTime' | 'endTime' | 'speed'>,
  pageTimeDeltaSeconds: number
): number {
  const trimWindow = videoItemObject.endTime - videoItemObject.startTime;
  if (trimWindow <= 0) {
    return 0;
  }
  const speed = videoItemObject.speed > 0 ? videoItemObject.speed : 1;
  const shiftedOffset = quantizeTimeSeconds(positiveModulo(videoItemObject.loopStartOffset + pageTimeDeltaSeconds * speed, trimWindow));
  return shiftedOffset >= trimWindow ? 0 : shiftedOffset;
}

/**
 * Absolute page times at which this looping video's bar start would land exactly on
 * a loop boundary (loopStartOffset 0) — the whole-pass anchor grid `anchor + k·D`,
 * clamped to [0, maxPageTime]. Empty for non-looping videos.
 */
export function getVideoItemLoopAnchorPageTimes(
  videoItemObject: Pick<VideoItemObject, 'startTime' | 'endTime' | 'speed' | 'isBackground' | 'loopStartOffset' | 'onPageStartTime'>,
  maxPageTime: number
): number[] {
  if (!isVideoLoopingEnabledForItem(videoItemObject)) {
    return [];
  }

  const singlePassDuration = getMotionItemPlaybackDuration(videoItemObject);
  if (singlePassDuration <= 0) {
    return [];
  }

  const speed = videoItemObject.speed > 0 ? videoItemObject.speed : 1;
  const anchor = videoItemObject.onPageStartTime - videoItemObject.loopStartOffset / speed;
  const firstIndex = Math.ceil(-anchor / singlePassDuration);
  const lastIndex = Math.floor((maxPageTime - anchor) / singlePassDuration);
  const anchorPageTimes: number[] = [];
  for (let anchorIndex = firstIndex; anchorIndex <= lastIndex; anchorIndex++) {
    anchorPageTimes.push(anchor + anchorIndex * singlePassDuration);
  }
  return anchorPageTimes;
}

/** A transcript's captions carry absolute page times tied to the media they transcribe, so its span can't be moved on its own. */
export function isSpanAnchoredToMedia(gitype: ITEM_TYPE): boolean {
  return gitype === ITEM_TYPE.TRANSCRIPT;
}

/** The item's span moved `offset` seconds along its page, its length unchanged. */
export function shiftItemObjectSpan(itemObject: ItemObject, offset: number): ItemObject {
  return {
    ...itemObject,
    onPageStartTime: quantizeTimeSeconds(itemObject.onPageStartTime + offset),
    onPageEndTime: quantizeTimeSeconds(getItemPageEndTime(itemObject) + offset),
  };
}

/** Quantized so derived ends (trim ÷ speed, slide-duration sums) and legacy raw floats all land on the millisecond grid the write paths use. */
export function getItemPageEndTime(itemObject: ItemObject): number {
  return quantizeTimeSeconds(getRawItemPageEndTime(itemObject));
}

/**
 * Whether a partial item object carries every field `getItemPageEndTime` reads for its type. A load
 * or history snapshot passes; a field patch does not. Narrows only as far as this module's timing
 * reads need — the other keys of a full item object are not checked.
 */
export function hasItemTimingFields(itemObject: DeepPartial<ItemObject>): itemObject is ItemObject {
  const hasBaseTiming =
    typeof itemObject.gitype === 'string' && typeof itemObject.onPageStartTime === 'number' && typeof itemObject.onPageEndTime === 'number' && typeof itemObject.isBackground === 'boolean';
  if (!hasBaseTiming) {
    return false;
  }
  if (itemObject.gitype === ITEM_TYPE.VIDEO) {
    return 'startTime' in itemObject && typeof itemObject.startTime === 'number' && typeof itemObject.endTime === 'number' && typeof itemObject.speed === 'number';
  }
  if (itemObject.gitype === ITEM_TYPE.SLIDESHOW) {
    return 'slides' in itemObject && Array.isArray(itemObject.slides?.slidesOrder) && itemObject.slides.slidesHashMap !== undefined;
  }
  return true;
}

function getRawItemPageEndTime(itemObject: ItemObject): number {
  if (isVideoItemObject(itemObject)) {
    if (isVideoLoopingEnabledForItem(itemObject)) {
      return itemObject.onPageEndTime;
    }
    return itemObject.onPageStartTime + getMotionItemPlaybackDuration(itemObject);
  }
  if (isSlideshowItemObject(itemObject)) {
    return itemObject.onPageStartTime + getSlideshowDurationFromObject(itemObject);
  }
  return itemObject.onPageEndTime;
}
